import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ShieldCheck, RefreshCw, Truck, Calendar, Search, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRICE = 31.00;

export default function Subscribe() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_email: '', first_name: '', last_name: '', phone: '',
    address_line_1: '', address_line_2: '', city: '', county: '', postcode: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [postcodeLoading, setPostcodeLoading] = useState(false);
  const [postcodeError, setPostcodeError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const lookupPostcode = async () => {
    const code = (form.postcode || '').trim().toUpperCase();
    if (!code) { setPostcodeError('Enter a postcode first.'); return; }
    setPostcodeLoading(true); setPostcodeError('');
    try {
      // Opt out of `withCredentials` for the cross-origin postcodes.io call
      // (axios.defaults sets it true globally for our /api calls; postcodes.io
      // returns Access-Control-Allow-Origin: * which the browser rejects when
      // credentials mode is 'include').
      const r = await axios.get(`https://api.postcodes.io/postcodes/${encodeURIComponent(code)}`, { withCredentials: false });
      const result = r.data?.result;
      if (!result) throw new Error('Not found');
      setForm((prev) => ({
        ...prev,
        city: prev.city || result.post_town || result.parish || '',
        county: prev.county || result.admin_county || result.region || '',
        postcode: result.postcode || code,
      }));
      toast.success(`Address found: ${result.post_town || ''}`);
    } catch (err) {
      setPostcodeError(err.response?.status === 404 ? 'Postcode not found.' : 'Lookup failed.');
    } finally {
      setPostcodeLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/subscriptions/checkout`, {
        ...form,
        country: 'United Kingdom',
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subscription checkout failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] py-20 px-4" style={{ backgroundColor: '#f7f9f3' }} data-testid="subscribe-page">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors text-sm" data-testid="subscribe-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-3">Subscribe & save</span>
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-3">Monthly Auto-Ship</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">One bottle delivered to your door every month, charged at <strong className="text-emerald-700">£{PRICE.toFixed(2)}</strong>. 3-month minimum, then cancel any time.</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Plan summary */}
          <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-2 bg-white rounded-2xl border border-emerald-100 p-6 self-start" data-testid="subscribe-plan-summary">
            <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-2">Your plan</div>
            <div className="text-3xl font-bold text-slate-900 mb-1">£{PRICE.toFixed(2)} <span className="text-base font-normal text-slate-500">/ month</span></div>
            <div className="text-xs text-slate-500 line-through mb-4">£44.99 RRP</div>

            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><Truck className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" /><span>1 bottle (60 caps) &mdash; <strong>free UK delivery</strong></span></li>
              <li className="flex items-start gap-2"><Calendar className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" /><span>Same date every month, no surprises</span></li>
              <li className="flex items-start gap-2"><RefreshCw className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" /><span>3-month minimum (one quarter) &mdash; then cancel any time from your account</span></li>
              <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" /><span>Card details secured by Stripe (PCI DSS Level 1)</span></li>
            </ul>

            <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
              Your £{PRICE.toFixed(2)}/mo is all-inclusive of UK VAT (20%) and free standard delivery. You can update your shipping address or cancel any time &mdash; no questions asked.
            </div>
          </motion.div>

          {/* Form */}
          <motion.div initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-3 bg-white rounded-2xl border border-emerald-100 p-6">
            <h2 className="text-xl font-bold mb-4">Delivery & contact details</h2>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="subscribe-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input name="first_name" value={form.first_name} onChange={handleChange} required placeholder="First name" className="px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-firstname" />
                <input name="last_name" value={form.last_name} onChange={handleChange} required placeholder="Last name" className="px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-lastname" />
                <input name="customer_email" type="email" value={form.customer_email} onChange={handleChange} required placeholder="Email" className="sm:col-span-2 px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-email" />
                <input name="phone" value={form.phone} onChange={handleChange} required placeholder="Phone (UK mobile)" className="sm:col-span-2 px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-phone" />
                <input name="address_line_1" value={form.address_line_1} onChange={handleChange} required placeholder="Address line 1" className="sm:col-span-2 px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-addr1" />
                <input name="address_line_2" value={form.address_line_2} onChange={handleChange} placeholder="Address line 2 (optional)" className="sm:col-span-2 px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-addr2" />
                <input name="city" value={form.city} onChange={handleChange} required placeholder="Town / City" className="px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-city" />
                <input name="county" value={form.county} onChange={handleChange} placeholder="County" className="px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-county" />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Postcode</label>
                <div className="flex gap-2">
                  <input name="postcode" value={form.postcode} onChange={handleChange} required placeholder="NN1 2AB" className="flex-1 px-3 py-2.5 rounded-lg border border-slate-300 uppercase focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" data-testid="subscribe-postcode" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupPostcode(); } }} />
                  <button type="button" onClick={lookupPostcode} disabled={postcodeLoading || !form.postcode} className="px-4 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white text-sm font-medium flex items-center gap-1.5" data-testid="subscribe-postcode-lookup">
                    {postcodeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}<span className="hidden sm:inline">Find</span>
                  </button>
                </div>
                {postcodeError && <p className="text-xs text-red-600 mt-1">{postcodeError}</p>}
              </div>

              <button type="submit" disabled={submitting} data-testid="subscribe-submit" className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-white font-semibold transition disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {submitting ? 'Redirecting to Stripe\u2026' : `Continue to secure checkout \u2014 £${PRICE.toFixed(2)}/mo`}
              </button>

              <p className="text-xs text-slate-500 text-center">
                By subscribing you agree to recurring monthly billing. Prefer a one-time order? <Link to="/products" className="text-emerald-700 hover:underline">Browse products</Link>.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
